// no itx for this file
import { KnxProductConfig } from "./KnxProductConfig";
import KnxLanguageMapping from "./KnxProductDataTools/LanguageMapping";
import { hasExcludedSceneName } from "./KnxProductDataTools/hasExcludedSceneName";
import determineActorComObjectFunction from "./KnxProductDataTools/determineActorComObjectFunction";
import determineButtonComObjectFunction from "./KnxProductDataTools/determineButtonComObjectFunction";
import Logger from "./Logger";
import { firstChildWithTag, Tag } from "./components/XmlUtils";
import BaseModel from "./models/BaseModel";
import Device from "./models/Device";
import KnxRule, { RuleIds } from "./models/KnxRule";
import KnxRules from "./models/KnxRules";
import { channelMethode } from "./models/SettingsEnums";
import Signal from "./models/Signal";
import { copyAllProperties } from "./models/utils";
import ProductStorageHandler from "./persistence/ProductStorageHandle";
import DeviceTemplate from "./models/DeviceTemplate";
import { showOfflineStatus } from "./components/OfflineStatus";
import XMLParserSingleton from "./XMLParserSingleton";
import Channel from "./models/Channel";
import { isUiModeProductAdmin } from "./uiMode";
import { isParameterType, isTypeRestriction, ParameterType, TypeRestriction } from "./Types";
import { isEncoded } from "./utils/CommonUtils";
import { dataUrlToImage } from "./modelViews/GraficElementUtils";
import getManufacturRule from "./KnxMappings/ManufactorRules";
import KnxProductInfo, { getEncodedOriginalMetadata, type KnxProductRetailerData } from "./models/KnxProductInfo";
import { ChannelType } from "./models/ChannelType";
import { KnxGlobalScriptEngine } from "./KnxGlobalScriptEngine";

// Manuelle Mappings von ComObj Number to estada SiganlType

export type LabelInfo = {
  label: string;
  index: number;
  id: string;
  instanceId: string | undefined;
};

export class KnxParameterUnion {
  parameterIds = new Array<string>();
  parameterRefIds = new Array<string>();
  id = "";
}

export function ConvertVersion(ApplicationVersion: string | undefined): string {
  if (ApplicationVersion) {
    return `${Math.floor(parseInt(ApplicationVersion) / 16)}.${parseInt(ApplicationVersion) % 16}`;
  } else {
    return "-";
  }
}

const logger = new Logger();
export default class KnxProductData extends BaseModel {
  comObjectRefRefMap: Map<string, any>;
  moduleInstanceMap: Map<string, Array<any>>;
  public get className(): string {
    return "KnxProductData";
  }

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    this.comObjectRefRefMap = new Map<string, any>();
    this.moduleInstanceMap = new Map<string, Array<any>>();

    copyAllProperties(data, this);
  }

  private _modifiedRules = new KnxRules(this);
  public get modifiedRules() {
    return this._modifiedRules;
  }
  public set modifiedRules(value) {
    this._modifiedRules = value;
  }

  private mergedRules: KnxRules | undefined;

  mergeRules() {
    const serverRules = this.config.rules?._rules;

    if (!this.mergedRules) {
      this.mergedRules = new KnxRules(this);
    }

    this.mergedRules.isPersistent = false;

    this.mergedRules.init();

    if (serverRules) {
      serverRules.forEach((value, key) => {
        this.mergedRules?._rules?.set(key, value);
      });
    }

    // console.log("mergeRules");

    const deleteKeys = new Array<string>();

    this.modifiedRules._rules?.forEach((modifiedRule, key) => {
      modifiedRule.modifiedLocal = true;

      const serverRule = serverRules?.get(key);
      if (serverRule && serverRule.lastModified.getTime() > modifiedRule.lastModified.getTime()) {
        //
        // if the rule on the sever is newer than the local modified rule
        // we keep the rule from the server,
        // delete afterwards the local modified rule
        //
        deleteKeys.push(key);
      } else {
        this.mergedRules?._rules?.set(key, modifiedRule);

        // console.log(`mergeRules ${key} ${value.label} localDate ${value.lastModified} serverDate ${serverRule?.lastModified}`);
      }
    });

    deleteKeys.forEach((key) => {
      this.modifiedRules._rules?.delete(key);
    });
  }

  get currentRules(): KnxRules | undefined {
    //
    // first time merge rules stored on the server
    // "local" / modified rules overrides rules stored on server
    //

    if (this.mergedRules == undefined) {
      this.mergeRules();
    }

    return this.mergedRules;
  }

  propertyLabels = new Map<string, LabelInfo>();

  setPropertyLabel(index: number, label: string, id: string, instanceId: string | undefined) {
    this.propertyLabels.set(`${id} - ${instanceId}`, { index: index, label: label, id: id, instanceId: instanceId });
  }

  getPropertyLabel(id: string, instanceId: string | undefined): LabelInfo | undefined {
    return this.propertyLabels.get(`${id} - ${instanceId}`);
  }

  /////////////////////////////////////////////////////////////////////

  get hasScript_mapSignalToTag(): boolean {
    const ruleControlChannelTypeCode = this.currentRules?.get(RuleIds.mapSignalToTag)?.code?.trim() || "";
    if (ruleControlChannelTypeCode != "") return true;
    // NoChannels acts as an implicit ignore-all script
    return this.productInfo?.NoChannels === "true";
  }

  get hasScript_setChannelType(): boolean {
    const ruleControlChannelTypeCode = this.currentRules?.get(RuleIds.setChannelType)?.code?.trim() || "";
    return ruleControlChannelTypeCode != "";
  }

  get hasScript_panelConfig(): boolean {
    const ruleControlChannelTypeCode = this.currentRules?.get(RuleIds.panelConfig)?.code?.trim() || "";
    return ruleControlChannelTypeCode != "";
  }

  get hasScript_mapChannelType(): boolean {
    const ruleControlChannelTypeCode = this.currentRules?.get(RuleIds.mapChannelType)?.code?.trim() || "";
    return ruleControlChannelTypeCode != "";
  }

  get hasScript_mapChannelText(): boolean {
    const s = this.currentRules?.get(RuleIds.mapChannelText)?.code?.trim() || "";
    return s != "";
  }

  get hasScript_setRockerConfig(): boolean {
    const ruleControlRockerConfigCode = this.currentRules?.get(RuleIds.setRockerConfig)?.code?.trim() || "";
    return ruleControlRockerConfigCode != "";
  }

  get hasScript_mapChannelToTag(): boolean {
    const ruleMapChannelToTagCode = this.currentRules?.get(RuleIds.mapChannelToTag)?.code?.trim() || "";
    return ruleMapChannelToTagCode != "";
  }
  get hasScript(): boolean {
    return (
      this.hasScript_mapChannelToTag ||
      this.hasScript_mapSignalToTag ||
      this.hasScript_setChannelType ||
      this.hasScript_setRockerConfig
    );
  }

  _buttonToComObjMapping: any | undefined;
  buttonToComObjMappingLoaded?: boolean;

  //////////////////////////////////////////////////////////////////////
  // persistent data
  //

  applicationProgram = "";

  productId?: string;
  hardware2ProgramId?: string;

  prefix: string = "";
  prefixLength = 0;

  //
  // Tag <Script> in the ApplicationProgram
  //
  _knxScriptCode: string | undefined = undefined;
  get knxScriptCode(): string | undefined {
    return this._knxScriptCode;
  }
  set knxScriptCode(value: string | undefined) {
    this._knxScriptCode = value;
    if (value) {
      this.knxScriptEngine = new KnxGlobalScriptEngine(value);

      // const functionNames = this.knxScriptEngine.getFunctionNames();
      // console.log(functionNames);

      // const functionInfos = this.knxScriptEngine.getFunctionInfos();
      // console.log(functionInfos);
    }
  }

  knxScriptEngine: KnxGlobalScriptEngine | undefined = undefined;

  // Scene wird deactiviert durch eine nummer < 0 oder > 63
  // wenn dem so ist, kann man nicht allgemeingüligt festlegen,
  // was dieser Wert ist, sondern muss das aus den ProductDAten analysieren
  //
  sceneDisabledValue: number = 0;
  sceneNumberOffset: number = 0;
  sceneDisabledValueIsAnalysed = false;

  ///////////////////////////////////////////////////////////////
  // THE ProductData XML
  //
  // the xml maybe stored localy here (unusual in the future)
  // or this dynamicly loaded from our product database

  modelDataId: string | undefined = undefined;
  xmlstring: string | undefined = undefined;

  ///////////////////////////////////////////////////////////////
  // Baggage.xml Images

  imageId: string | undefined = undefined;
  imagestring: string | undefined = undefined;

  frontImage: string | undefined = undefined;
  frontImageSize = { width: 0, height: 0 };
  iconFileSrc: any = {};

  frontImageLoaded = false;
  frontImageSource = "";
  frontImageSourceDetail = "";

  get frontImageSourceDescription(): string {
    if (!this.frontImageSource) return "";
    return this.frontImageSourceDetail
      ? `${this.frontImageSource} (${this.frontImageSourceDetail})`
      : this.frontImageSource;
  }

  private setFrontImageSource(source: string, detail = "") {
    this.frontImageSource = source;
    this.frontImageSourceDetail = detail;
  }

  ///////////////////////////////////////////////////////////////

  getPersistentProperties(): string[] {
    return [
      "applicationProgram",
      "productInfo",
      "xmlstring",
      "modelDataId",
      "sceneDisabledValue",
      "calculations",
      "modifiedRules",
      "config",
      ...super.getPersistentProperties(),
    ];
  }

  //////////////////////////////////////////////////////////////////////
  _allSignals: Array<Signal> | undefined;

  private setParentsRecursively(node: any, parent = null): void {
    if (parent) {
      Object.defineProperty(node, "parent", { value: "static", writable: true, enumerable: false });
      node.parent = parent;
    }
    if (!node) {
      return;
    }

    if (node["#name"] == "ComObjectRefRef") {
      this.comObjectRefRefMap.set(node.a.RefId, node);
    }

    if (node["#name"] == "Module") {
      if (this.moduleInstanceMap.has(node.a.RefId)) {
        this.moduleInstanceMap.get(node.a.RefId)?.push(node);
      } else {
        this.moduleInstanceMap.set(node.a.RefId, [node]);
      }
    }

    node.items.forEach((child) => {
      this.setParentsRecursively(child, (parent = node));
    });
  }

  addComObjectRefs(ComObjectRefs: any, dummyChannel: Channel, moduleId?: string) {
    for (let i in ComObjectRefs.items) {
      const item = ComObjectRefs.items[i];
      const id = item.a.Id;
      const refId = item.a.RefId;

      const ComObject = this.deref(item);

      //  Id="M-0083_A-0098-11-29A3_O-40" Name="Eingangslogik 1 A" Text="Logic" Number="40" FunctionText="Input 1 A" ObjectSize="1 Bit" ReadFlag="Disabled" WriteFlag="Enabled" CommunicationFlag="Enabled" TransmitFlag="Enabled" UpdateFlag="Enabled" ReadOnInitFlag="Disabled" DatapointType="DPST-1-1" />
      const functionText = this.getTranslatedProperty("FunctionText", ComObject) || ComObject.a.FunctionText || "??";

      const text = this.getTranslatedProperty("Text", ComObject) || ComObject.a.Text || "??";

      ////////////////////////////////////////////
      // engTranslation

      const functionTextEN =
        this.getTranslatedProperty("FunctionText", ComObject, true) || ComObject.a.FunctionText || "??";

      const textEN = this.getTranslatedProperty("Text", ComObject, true) || ComObject.a.Text || "??";

      const signal = new Signal(
        {
          knxId: ComObject.a.Id,
          moduleId: moduleId,
          a: ComObject.a,
          knxTextEN: textEN,
          knxFunctionText: functionText,
          knxFunctionTextEN: functionTextEN,
          knxTextENForDetection_Template: textEN,
          knxFunctionTextOrg: ComObject.a.FunctionText,
          knxDatapointType: ComObject.a.DatapointType,
          knxNestedName: "unknown",
          name: text,
          number: ComObject.a.Number,
        },
        dummyChannel,
      );

      try {
        if ((this.parent as Device)?.isActor) {
          determineActorComObjectFunction(signal);
        } else {
          determineButtonComObjectFunction(signal);
        }
      } catch (ex) {
        logger.error("determineComObjectFunctions", ex);
      }

      this._allSignals?.push(signal);
    }
  }

  async allSignals(device: Device): Promise<Array<Signal>> {
    if (this._allSignals) {
      return this._allSignals;
    }

    this._allSignals = new Array<Signal>();

    const data = this.getApplicationProgramIntern(device.applicationProgramId);

    const dummyDevice = new Device();
    dummyDevice.tag = "@T";
    dummyDevice.templateId = this.parent?.id || ""; // as DeviceTemplate;
    const dummyChannel = new Channel(undefined, dummyDevice);

    //
    // Add all Global ComObjects
    //
    const ComObjectRefs = data.Static.ComObjectRefs;
    if (ComObjectRefs) {
      this.addComObjectRefs(ComObjectRefs, dummyChannel);
    }

    var applicationProgram = this.getApplicationProgramIntern(undefined);
    const moduleDefs = applicationProgram?.ModuleDefs;

    if (moduleDefs) {
      //
      // Add all ComObjects of all Modules
      //
      for (let i in moduleDefs.items) {
        const moduleDef = moduleDefs.items[i];
        const ComObjectRefs = moduleDef.Static.ComObjectRefs;
        if (ComObjectRefs) {
          // "M-0002_A-3002-11-7907-O0077" with orderId "2CDG 120 089 R0011" has a moduleDef with no ComObjectRefs in it
          this.addComObjectRefs(ComObjectRefs, dummyChannel, moduleDef.a.Id);
        }
        //  else {
        //   console.log("No COR in ModuleDef");
        // }
      }
    }

    return this._allSignals;
  }

  ////////////////////////////////////////////////////////////////////////////

  config = new KnxProductConfig(this);

  // To access Metadata of Product, use productInfo. E.g.  productInfo.BusCurrent;
  // const updatedMetaData = {
  //   ...KnxProductData.productInfo
  //   "BusCurrent":"20"
  // }
  // To update metadata in azure store:
  // updateProductMetaData(productId, updatedMetaData);

  productInfo = new KnxProductInfo();

  private retailerRequests = new Map<string, Promise<KnxProductRetailerData | undefined>>();
  private retailerLoaded = new Set<string>();

  /** Loads one shared price/availability snapshot; UI and exports use the same article variant. */
  async loadPriceAndAvailability(articleNo = this.productInfo.OrderNumber, refresh = false): Promise<KnxProductRetailerData | undefined> {
    if (!articleNo) return undefined;
    const stored = this.productInfo.RetailerData?.[articleNo];
    if (stored && this.retailerLoaded.has(articleNo) && !refresh) return stored;
    const running = this.retailerRequests.get(articleNo);
    if (running) return running;
    const request = (async () => {
      try {
        const row = await ProductStorageHandler.lookupVoltusByArticleNo(articleNo);
        if (!row) return stored;
        const data: KnxProductRetailerData = {
          price: row.Price, availability: row.Availibility, ean: row.EAN, page: row.Page,
          loadedAt: new Date().toISOString(),
        };
        this.productInfo.RetailerData = { ...this.productInfo.RetailerData, [articleNo]: data };
        this.retailerLoaded.add(articleNo);
        return data;
      } catch {
        return stored;
      } finally {
        this.retailerRequests.delete(articleNo);
      }
    })();
    this.retailerRequests.set(articleNo, request);
    return request;
  }

  setProductInfoFromLowercase(data: any) {
    //
    // convert all lowercase fieldnames to orignal names
    //

    this.productInfo = new KnxProductInfo();

    for (let camelcaseName in this.productInfo) {
      const d = data[camelcaseName.toLowerCase()];
      (this.productInfo as any)[camelcaseName] = d;
    }
  }

  // setProductInfo(metadata: any) {
  //   this.productInfo = metadata;
  //   if (this.modelDataId) {
  //     ProductStorageHandler.storeCachedMetaDataAsObject(this.modelDataId, this.productInfo);
  //   }
  // }

  private _modelMetadata: any | undefined = undefined;

  async getModelMetadata(device?: Device): Promise<any | undefined> {
    if (this._modelMetadata == undefined) {
      this._modelMetadata = await this.loadXmlData(device);
    }

    return this._modelMetadata;
  }

  //////////////////////////////////////////////////////////////////////////////

  private nonPersistentXmlstring: string | undefined;

  private downloadIsRunning = false;
  async loadXmlstring(): Promise<string | undefined> {
    var s: string | undefined;
    if (this.nonPersistentXmlstring == undefined) {
      if (this.xmlstring) {
        return this.xmlstring;
      }
      if (this.modelDataId) {
        if (!navigator.onLine) {
          showOfflineStatus(true);
          return undefined;
        } else {
          showOfflineStatus(false);
        }

        logger.log(`downloadProductData ${this.modelDataId} ${this.downloadIsRunning}`);
        this.downloadIsRunning = true;
        const p = ProductStorageHandler.downloadProductData(this.modelDataId);
        s = await p;

        // LOad Parameter Icons
        await this.loadIconFile();
        await this.loadFrontImage();

        this.downloadIsRunning = false;
        this.nonPersistentXmlstring = s;

        await this.loadConfig();
      }
      return s ? s : undefined;
    } else {
      return this.nonPersistentXmlstring;
    }
  }

  async loadMetadata() {
    if (this.modelDataId) {
      const data = await ProductStorageHandler.getProductMetaData(this.modelDataId);
      if (data) {
        data.RetailerData = { ...data.RetailerData, ...this.productInfo.RetailerData };
        this.productInfo = data;
        // this.setProductInfoFromLowercase(rawData);
      } else {
        logger.error(`loadMetadata FAILED for: ${this.modelDataId} `);
        state.logConsistencyError(this.parent as BaseModel, `loadMetadata FAILED for: ${this.modelDataId} `);
      }

      // const metadataAsObject = await ProductStorageHandler.loadCachedMetaDataAsObject(this.modelDataId);
      // if (metadataAsObject) {
      //   this.productInfo = metadataAsObject;
      // }
    }
  }

  async loadConfig() {
    if (this.modelDataId && this.productInfo.HasConfig == "true") {
      let resServer: string | undefined = "";
      try {
        const resLocal = await ProductStorageHandler.loadCachedProductDataAsObject("C" + this.modelDataId);

        if (state.projectMetadata.loadedFrom == "local") {
          //
          // Wenn das Project lokal geladen wurde,
          // verzichten wir darauf die Config Daten vom Server zu laden.
          //
          // Dies ist generell überhaupt nur deswegen nötig,
          // weil sich die Config durch unsere Customizing Aktivitäten geändert haben könnte.
          // Daher solle es ok sein darau zu verzichten wenn wir mit einem Offline Projekt arbeiten
          //
          //

          if (resLocal) {
            this.config.fromJSON(resLocal);
            this.mergeRules();
            return;
          }
        }

        logger.log("Load config for product:", this.modelDataId);
        resServer = await ProductStorageHandler.downloadProductConfig(this.modelDataId);
        if (resServer) {
          const p_configObj = JSON.parse(resServer);

          ProductStorageHandler.storeCachedProductDataAsObject("C" + this.modelDataId, p_configObj);

          this.config.fromJSON(p_configObj);
          this.mergeRules();
        }
      } catch (ex) {
        logger.error("Error loading config for product: " + this.modelDataId, ex, resServer);
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////

  private nonPersistentImage: string | undefined;
  private imageDownloadIsRunning = false;

  async loadImage(): Promise<string | undefined> {
    var s: string | undefined;
    if (this.nonPersistentImage == undefined) {
      if (this.imagestring) {
        return this.imagestring;
      }

      if (this.imageId) {
        logger.log(`downloadImageData ${this.imageId} ${this.imageDownloadIsRunning} `);
        try {
          this.imageDownloadIsRunning = true;
          const p = ProductStorageHandler.downloadImageData(this.imageId, this.productInfo.CatalogId);
          s = await p;
          this.imageDownloadIsRunning = false;
          this.nonPersistentImage = s;
        } catch (ex) {
          logger.error(ex);
        }
      }
      return s ? s : undefined;
    } else {
      return this.nonPersistentImage;
    }
  }

  async loadIconFile(iconFileParam?: string, zipArrayBuffer?: any) {
    if (iconFileParam) {
      this.productInfo["IconFile"] = iconFileParam;
    }
    if (this.productInfo?.IconFile) {
      const iconFiles = await ProductStorageHandler.downloadImageZip(
        this.productInfo?.IconFile,
        this.productInfo?.FromEts == "true",
        zipArrayBuffer,
      );
      for (let filename in iconFiles) {
        const imageFile = iconFiles[filename];
        const imageData = await imageFile.async("blob");
        const objectURL = URL.createObjectURL(imageData);
        this.iconFileSrc[filename] = objectURL;
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////

  nonPersistentXmlData: any | undefined;
  private loadXmlDataPromise: Promise<any | undefined> | undefined;

  get loadedXmlData(): any | undefined {
    return this.nonPersistentXmlData;
  }

  async loadDataFromLocalCache(device?: Device) {
    const applicationProgramId = device?.applicationProgramId || "";
    const pAsObject = await ProductStorageHandler.loadCachedProductDataAsObject(
      this.modelDataId + applicationProgramId,
    );
    return pAsObject;
  }

  loadXmlData(device?: Device): Promise<any | undefined> {
    if (this.loadXmlDataPromise) return this.loadXmlDataPromise;

    const promise = this.loadXmlDataCore(device);
    this.loadXmlDataPromise = promise;
    void promise.then(
      () => {
        if (this.loadXmlDataPromise === promise) this.loadXmlDataPromise = undefined;
      },
      () => {
        if (this.loadXmlDataPromise === promise) this.loadXmlDataPromise = undefined;
      },
    );

    return promise;
  }

  private async loadXmlDataCore(device?: Device): Promise<any | undefined> {
    if ((this.parent as DeviceTemplate)?.genericParams) return undefined;
    let result: any = undefined;

    const applicationProgramId = device?.applicationProgramId || "";

    if (this.nonPersistentXmlData) {
      return this.nonPersistentXmlData;
    } else {
      //
      // try to use object cache for productdata
      //
      if (this.modelDataId) {
        //
        // try to load productData from localStorage
        // this is much faster, as caching, and parsing ths xml string
        //

        const pAsObject = await ProductStorageHandler.loadCachedProductDataAsObject(
          this.modelDataId + applicationProgramId,
        );
        if (pAsObject) {
          this.nonPersistentXmlData = pAsObject;

          // const metadataAsObject = await ProductStorageHandler.loadCachedMetaDataAsObject(this.modelDataId);
          // if (metadataAsObject) {
          //   this.productInfo = metadataAsObject;
          // }
          result = pAsObject;
        }
      }

      if (!result) {
        //
        // normal loading via xml string
        //
        var s: string | undefined = await this.loadXmlstring();
        if (s) {
          this.nonPersistentXmlstring = s;
          const data = await KnxProductData.convertAppXmlstringToJson(s);

          this.nonPersistentXmlData = data;

          const applicationProgram = this.getApplicationProgramIntern(applicationProgramId);
          try {
            // if (applicationProgram.a?.IconFile) {
            //   this.productInfo["IconFile"] = applicationProgram.a.IconFile;
            // }
            // await this.loadIconFile();
            await this.loadFrontImage();
            this.prefix = applicationProgram.a.Id;
            this.prefixLength = this.prefix.length + 1;
          } catch (ex) {
            logger.error(ex);
          }

          if (this.prefixLength > 1) {
            try {
              const ManufacturerData = firstChildWithTag(this.nonPersistentXmlData.KNX, "ManufacturerData");
              const Manufacturer = firstChildWithTag(ManufacturerData, "Manufacturer");
              this.stripAllIds(Manufacturer);
            } catch (ex) {
              logger.error(ex);
            }
          } else {
            logger.error("strip twice !!!??", this.productInfo.OrderNumber);
          }
          try {
            applicationProgram.a.Id = this.prefix;
          } catch (ex) {
            logger.error(ex);
          }
          if (this.modelDataId) {
            ProductStorageHandler.storeCachedProductDataAsObject(this.modelDataId + applicationProgramId, data);
            // ProductStorageHandler.storeCachedMetaDataAsObject(this.modelDataId, this.productInfo);

            //
            // to save memory....
            // if the xml string is converterd to "this.nonPersistentXmlData"
            // it is not used anymore since the xml is stored on the server
            //
            // if the productData in not stored on the server (!this.modelDataId), we keep it!!!
            //

            // this.xmlstring = undefined;
          }

          this.raiseOnChange("loaded");

          result = data;
          if (!result) {
            this.globalMapLoaded = true; // avoid createRefMap from working

            logger.error("Failed loading Productdata: ", this.modelDataId);
            return undefined;
          }
        } else {
          this.failedLoadingProductData = true;
          this.globalMapLoaded = true; // avoid createRefMap from working
          logger.error("Failed loading Productdata: ", this.modelDataId);
          return undefined;
        }
      } else {
        this.loadIconFile();
      }

      if (isUiModeProductAdmin() && this.comObjectRefRefMap.size == 0 && this.moduleInstanceMap.size == 0) {
        this.setParentsRecursively(this.getApplicationProgramIntern(applicationProgramId));
      }

      this.createRefMap(device);

      //
      // load metadata allways from server to get especially changes in config
      //
      //

      await this.loadMetadata();

      //
      // load config
      //

      await this.loadConfig();

      return this.nonPersistentXmlData;
    }
  }

  async loadFrontImage(): Promise<boolean> {
    if ((this.parent as DeviceTemplate)?.genericParams) return false;
    try {
      if (this.frontImageLoaded) {
        return this.frontImage != undefined;
      }

      const template = this.parent as DeviceTemplate;
      this.frontImage = await ProductStorageHandler.loadFrontImage({
        orderNumber: this.productInfo.OrderNumber,
        catalogId: this.productInfo.CatalogId,
        articleNo: template?.orderInformation?.articleNo,
        onImageSource: (source, detail) => this.setFrontImageSource(source, detail),
      });

      const img = await dataUrlToImage(this.frontImage);
      if (img) {
        this.frontImageSize.width = img.width;
        this.frontImageSize.height = img.height;
      }

      //
      // use this boolean to avoid double loaded of non existing Images
      //
      this.frontImageLoaded = true;

      return this.frontImage != undefined;
    } catch (ex) {
      logger.error(ex);
    }
    return false;
  }

  static async convertAppXmlstringToJson(xmlstring: string): Promise<any | undefined> {
    // 06.03.2025: For Products having AP and AP2, we now store both xmls separated by <AP2StartsHere> tag.
    // Before processing we split the two xmls, convert both zo json and merge into single JSON object.
    let ap1data;
    const xmlstrings = xmlstring.split("<AP2StartsHere>");

    const parser = XMLParserSingleton.Instance;
    ap1data = parser.parse(xmlstrings[0]);
    if (xmlstrings.length == 2) {
      let ap2Data = parser.parse(xmlstrings[1]);
      const AP2ManufacturerData = firstChildWithTag(ap2Data.KNX, "ManufacturerData");
      const AP2Manufacturer = firstChildWithTag(AP2ManufacturerData, "Manufacturer");
      const AP2ApplicationPrograms = firstChildWithTag(AP2Manufacturer, "ApplicationPrograms");
      const AP2ApplicationProgram = firstChildWithTag(AP2ApplicationPrograms, "ApplicationProgram");
      const AP2Static = firstChildWithTag(AP2ApplicationProgram, "Static");
      const AP2Code = firstChildWithTag(AP2Static, "Code");
      const AP2LoadProcedures = firstChildWithTag(AP2Static, "LoadProcedures");

      let AP1 = ap1data?.KNX.ManufacturerData.Manufacturer.ApplicationPrograms.ApplicationProgram;
      if (AP1) {
        ap1data.KNX.ManufacturerData.Manufacturer.ApplicationPrograms.ApplicationProgram.a["ApplicationVersion2"] =
          AP2ApplicationProgram?.a.ApplicationVersion;
      }
      ap1data?.KNX.ManufacturerData.Manufacturer.ApplicationPrograms.ApplicationProgram.Static.Code.items.push(
        ...AP2Code?.items,
      );
      ap1data?.KNX.ManufacturerData.Manufacturer.ApplicationPrograms.ApplicationProgram.Static.LoadProcedures.items.push(
        ...AP2LoadProcedures?.items,
      );
    }
    return ap1data;
  }

  get setModuleParametersAsReferenced() {
    //
    // NUR für MDT geben wir hier im Moment hart gecoded true zurück
    //
    return this.productInfo.ManufacturerId == "0083";
  }

  static async convertAppXmlstringToJson_New_NotUsed(
    xmlstring: string,
    ApplicationProgramId: string,
  ): Promise<any | undefined> {
    // 06.03.2025: For Products having AP and AP2, we now store both xmls separated by <AP2StartsHere> tag.
    // Before processing we split the two xmls, convert both zo json and merge into single JSON object.

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // MWE 2.2.2026
    // For Products having multiple APs, we now store ALL xmls separated by <AP2StartsHere> tag.
    // after the <AP2StartsHere> tag theres is ApplicationProgramId + "\n" appended
    //
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////

    const xmlstrings = xmlstring.split("<AP2StartsHere>");

    const parser = XMLParserSingleton.Instance;

    for (let i = 1; i < xmlstrings.length; i++) {
      if (xmlstrings[i].startsWith(ApplicationProgramId)) {
        const appData = parser.parse(xmlstrings[i]);
        return appData;
      }
    }

    const appData = parser.parse(xmlstrings[0]);
    return appData;

    // if (xmlstrings.length == 2) {
    //   let ap2Data = parser.parse(xmlstrings[1]);
    //   const AP2ManufacturerData = firstChildWithTag(ap2Data.KNX, "ManufacturerData");
    //   const AP2Manufacturer = firstChildWithTag(AP2ManufacturerData, "Manufacturer");
    //   const AP2ApplicationPrograms = firstChildWithTag(AP2Manufacturer, "ApplicationPrograms");
    //   const AP2ApplicationProgram = firstChildWithTag(AP2ApplicationPrograms, "ApplicationProgram");
    //   const AP2Static = firstChildWithTag(AP2ApplicationProgram, "Static");
    //   const AP2Code = firstChildWithTag(AP2Static, "Code");
    //   const AP2LoadProcedures = firstChildWithTag(AP2Static, "LoadProcedures");

    //   let AP1 = ap1data?.KNX.ManufacturerData.Manufacturer.ApplicationPrograms.ApplicationProgram;
    //   if (AP1) {
    //     ap1data.KNX.ManufacturerData.Manufacturer.ApplicationPrograms.ApplicationProgram.a["ApplicationVersion2"] =
    //       AP2ApplicationProgram?.a.ApplicationVersion;
    //   }
    //   ap1data?.KNX.ManufacturerData.Manufacturer.ApplicationPrograms.ApplicationProgram.Static.Code.items.push(
    //     ...AP2Code?.items,
    //   );
    //   ap1data?.KNX.ManufacturerData.Manufacturer.ApplicationPrograms.ApplicationProgram.Static.LoadProcedures.items.push(
    //     ...AP2LoadProcedures?.items,
    //   );
    // }
    // return ap1data;
  }

  // memory = new Map<string, KnxParameterUnion>();

  globalMap = new Map<string, any>();
  globalMapLoaded = false;
  failedLoadingProductData = false;

  // getUnion(parameterId: string): KnxParameterUnion | undefined {
  //   return this.memory.get(parameterId);
  // }

  async createRefMapDelayed(device: Device): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const createRefMapInterval = setInterval(() => {
        if (this.globalMapLoaded) {
          //
          // die daten wurden schon von einem paralel laufenden Interval geladen
          //
          clearInterval(createRefMapInterval);
          resolve(true);
          return;
        } else if (this.createRefMapRunning) {
          //
          // Ein paralel laufendes Interval läde gerade die Daten ein
          //
          // wir tuen nicht,
          // und warten weitere X x 100ms bis das erste Interval, dass noch läuft,
          // seine Arbeit abgeschlossen hat
          //
        } else {
          //
          // Lade die Daten
          //
          this.createRefMap(device).then(() => {
            clearInterval(createRefMapInterval);
            resolve(true);
            return;
          });
        }
      }, 100);
    });
  }

  createRefMapRunning = false;
  async createRefMap(device?: Device): Promise<void> {
    if (this.globalMapLoaded) {
      return;
    }
    this.createRefMapRunning = true;
    const modelMetadata = await this.getModelMetadata(device);

    this.globalMap = new Map<string, any>();

    if (modelMetadata) {
      var applicationProgram = this.getApplicationProgramIntern(device?.applicationProgramId);
      if (applicationProgram) {
        this.xCreateRefMap(applicationProgram, this.globalMap);
        this.globalMapLoaded = this.globalMap.size > 0;

        const parameters = applicationProgram?.Static?.Parameters;
        this.analyseUnions(parameters);

        const moduleDefs = applicationProgram?.ModuleDefs;

        if (moduleDefs) {
          for (let i in moduleDefs.items) {
            const moduleDef = moduleDefs.items[i];
            const parameters = moduleDef.Static?.Parameters;
            this.analyseUnions(parameters);
          }
        }
      }
    }
    this.createRefMapRunning = false;
  }

  analyseUnions(parameters: any) {
    if (parameters) {
      const parameterItems = parameters.items;

      for (let i in parameterItems) {
        const union = parameterItems[i];
        if (Tag(union) == "Union") {
          const unionItems = union.items;
          for (let j in unionItems) {
            const parameter = unionItems[j];
            if (Tag(parameter) == "Parameter") {
              parameter.union = union;
            }
          }
        }
      }
    }
  }

  xCreateRefMap(data: any, refMap: any) {
    for (let i in data.items) {
      const item = data.items[i];
      if (item && typeof item === "object") {
        if (item.a?.Id) {
          // shows empty because of index are >32bit
          refMap.set(item.a.Id, item);
          //refMap[item.a.Id] = item;
        }
        this.xCreateRefMap(item, refMap);
      }
    }
  }

  getItem(id: string | undefined): any {
    if (id) {
      return this.globalMap.get(id);
    } else {
      return undefined;
    }
  }

  deref(item: any): any {
    if (item) {
      if (typeof item == "string") {
        return this.globalMap.get(item);
      }

      // make a copy of the referenced object

      const RefId = item.a?.RefId || item.a?.ParamRefId;

      if (RefId == undefined) {
        return item;
      }

      let derefItem = this.globalMap.get(RefId);

      if (!derefItem) {
        logger.error("Deref Error", item);
        return undefined;
      }

      var res = item;
      res.a = { ...item.a }; // clone attributes, dont touch the original

      if (derefItem.a) {
        // doppelte Dereferenzierung !?
        if (derefItem.a.RefId) {
          derefItem = this.deref(derefItem);
        }

        // patch all overide values
        for (let pi in derefItem.a) {
          if (pi != "RefId" && pi != "Id" && pi != "ParamRefId") {
            if (res.a == undefined) {
              res.a = {};
            }
            //
            // wir kopieren das Attributes Dereferenzierten Objects nur
            // wenn das Attribut noch nich vorhanden ist.
            // da der Wert des Attributs des "abgeleiteten" Object gewinnt
            //
            if (res.a[pi] == undefined) {
              res.a[pi] = derefItem.a[pi];
            }
          }
        }
      }

      return res;
    }

    return undefined;
  }

  getDefaultValueForParameter(parameterData: any, instanceId: string): string | undefined {
    let value = parameterData.a.InitialValue;

    if (value != undefined) {
      // logger.log(`"InitialValue" found ${ item.a.Id } `);
      return value;
    }

    value = parameterData.a.Value;
    if (value != undefined) {
      // logger.log(`"value" found ${ item.a.Id } `);
      return value;
    }

    return this.getDefaultValueForParameter1(parameterData.a.RefId, instanceId);
  }

  getDefaultValueForParameter1(RefId: string, instanceId: string): string | undefined {
    if (RefId && RefId != "") {
      const derefItem = this?.globalMap.get(RefId);
      if (derefItem != undefined) {
        return this.getDefaultValueForParameter(derefItem, instanceId);
      } else {
        // logger.error(`getDefaultValueForOneP refref failed`, item);
      }
    }

    logger.error(`No value / initialValue for ${RefId}`);
    return undefined;
  }

  getApplicationProgramIntern(applicationProgramId?: string): any | undefined {
    const data = this.nonPersistentXmlData;
    const KNX = data?.KNX;
    const ManufacturerData = firstChildWithTag(KNX, "ManufacturerData");
    const Manufacturer = firstChildWithTag(ManufacturerData, "Manufacturer");
    const ApplicationPrograms = firstChildWithTag(Manufacturer, "ApplicationPrograms");

    if (ApplicationPrograms && ApplicationPrograms?.items?.length == 1) {
      const ApplicationProgram = ApplicationPrograms.items[0];
      // if (this.comObjectRefRefMap.size == 0 && this.moduleInstanceMap.size == 0) {
      //   this.setParentsRecursively(ApplicationProgram);
      // }

      return ApplicationProgram;
    }

    return undefined;
  }

  ///////////////////////////////////////////////////////////
  _languageMapping: KnxLanguageMapping | undefined;

  get languageMapping(): KnxLanguageMapping {
    if (!this._languageMapping || this._languageMapping.isEmpty) {
      this._languageMapping = new KnxLanguageMapping();
      this._languageMapping.load(this.loadedXmlData, state.settings.language);
      this._languageMapping.englishIsDefault =
        this.productInfo?.DefaultLanguage?.toLowerCase().startsWith("en") || false;
    }
    return this._languageMapping;
  }
  ///////////////////////////////////////////////////////////

  getParameterType(item: any): ParameterType | undefined {
    const derefItem = this.deref(item);

    var name: string = derefItem.a.Name;
    name = name.toLocaleLowerCase();

    const typeId = derefItem.a.ParameterType;
    const parameterType = this.deref(typeId);
    if (isParameterType(parameterType)) {
      return parameterType;
    } else {
      return undefined;
    }
  }

  getParameterEnumeration(item: any): TypeRestriction | undefined {
    const parameterType = this.getParameterType(item);
    if (parameterType == undefined) {
      return undefined;
    }

    const parameterTypeInner = parameterType.items[0];
    if (isTypeRestriction(parameterTypeInner)) {
      const typeRestriction: TypeRestriction = parameterTypeInner;
      return typeRestriction;
    } else {
      return undefined;
    }

    // const typeRestriction = parameterType?.TR || parameterType?.TypeRestriction;
    // const pameterEnumerations = pameterRestriction;

    // return typeRestriction;
  }

  getSceneDisabledValue(item: any): number {
    const pameterEnumerations = this.getParameterEnumeration(item);

    if (pameterEnumerations) {
      if (pameterEnumerations.items?.length == 65) {
        for (let i = 0; i < 65; i++) {
          let v = parseInt(pameterEnumerations?.items[i].a.Value);
          if (v > 63 || v < 0) {
            return v;
          }
        }
      }
    }

    return 255;
  }

  analyseSceneDisabledValue(items: Array<any>) {
    if (!this.sceneDisabledValueIsAnalysed) {
      this.sceneDisabledValue = 0;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const v = parseInt(item.a.Value);
        if (v < 0 || v > 63) {
          this.sceneDisabledValue = v;
        }

        if (v == 1) {
          if (item.a.Text?.indexOf("2") >= 0) {
            //
            // for MDT:
            //
            // <Enumeration Text="not active" Value="255" Id="M-0083_A-000A-21-85EA_PT-en.5FSceneValue_EN-255" />
            // <Enumeration Text="1" Value="0" Id="M-0083_A-000A-21-85EA_PT-en.5FSceneValue_EN-0" />
            // <Enumeration Text="2" Value="1" Id="M-0083_A-000A-21-85EA_PT-en.5FSceneValue_EN-1" />
            //
            // for others .... to be extended
            //

            this.sceneNumberOffset = -1;
          }
        }
      }

      this.sceneDisabledValueIsAnalysed = true;
    }
  }

  isSceneNumberItem(item: any): boolean {
    const parameter = this.deref(item);
    const parameterType = this.deref(parameter?.a?.ParameterType);
    // Addresses and counts can share the scene-number range without being scenes.
    if (hasExcludedSceneName(item?.a?.Name, parameter?.a?.Name, parameterType?.a?.Name)) {
      return false;
    }

    const pameterEnumerations = this.getParameterEnumeration(item);

    //
    // MDT uses Enumerations of SceneNumbers
    //
    const items = pameterEnumerations?.items;
    if (items) {
      if (items.length == 64 || items.length == 65) {
        this.analyseSceneDisabledValue(items);

        return true;
      }
    }

    //
    // ABB uses min/max restricted int for Scenesnumbers
    //
    const parameterTypeNumber = parameterType?.TypeNumber;
    if (parameterTypeNumber) {
      if (parameterTypeNumber.a.maxInclusive == "64") {
        return true;
      }
    }

    return false;
  }

  stripAllIds(data: any) {
    for (let i in data.items) {
      const item = data.items[i];
      if (item && typeof item === "object") {
        if (item.a) {
          for (let i in item.a) {
            const attr = item.a[i];
            if (attr.startsWith(this.prefix)) {
              const shortId = attr.substr(this.prefixLength);
              item.a[i] = shortId;
            }
          }
        }
        this.stripAllIds(item);
      }
    }
  }

  shortId(id: string): string {
    return id;
  }

  // get Text in selectet Language( if not available in default language)
  getTranslatedProperty(propertyName: string, data: any, en: boolean = false): string {
    var id = data?.a?.Id;

    let inlineValue = "";

    do {
      var text1: string | undefined;
      if (en) {
        text1 = this.languageMapping.getTranslationEN(id, propertyName);
      } else {
        text1 = this.languageMapping.getTranslation(id, propertyName);
      }
      if (text1) {
        return text1;
      }

      if (!inlineValue && data.a[propertyName]) {
        inlineValue = data.a[propertyName];
      }

      const RefId = data?.a?.RefId;
      if (RefId) {
        data = this.getItem(RefId);
        id = data.a.Id;
      } else {
        break;
      }
    } while (id);

    return inlineValue || "";
  }

  get supportScenes(): boolean {
    if (this.productInfo.SupportScenes) {
      return this.productInfo.SupportScenes.toLowerCase() != "false";
    }
    return true;
  }

  get channelDetectionMode(): channelMethode | undefined {
    if (this.productInfo.ChannelDetectionMode != undefined) {
      switch (this.productInfo.ChannelDetectionMode) {
        case channelMethode.name:
          return channelMethode.name;
        case channelMethode.bundle:
          return channelMethode.bundle;
      }
    }
    return undefined;
  }

  ////////////////////////////////////////////////////////////////////////////////////////////

  async saveMetadataOnProductServer(deviceTemplate: DeviceTemplate, device: Device): Promise<boolean> {
    if (this.modelDataId) {
      let knxInstanceData = device.knxInstanceData;
      // only rules has to be set from this device=> we need the default knxInstanceData
      if (!device.isDummyDeviceForTemplate) {
        let dummyDevice = new Device();
        dummyDevice.tag = "@T";
        await dummyDevice.setAndLoadTemplate(device.templateId || "");
        knxInstanceData = dummyDevice.knxInstanceData;
      }

      if (knxInstanceData) {
        this.config.saveParams(knxInstanceData);
        this.config.channelType = deviceTemplate.knxProductData.productInfo.ChannelType as ChannelType;
      }

      //
      // copy modified rules to config to save them to the server
      //

      this.mergeRules();

      if (this.mergedRules && this.mergedRules._rules && this.mergedRules._rules.size > 0) {
        const rNew = new KnxRules(this);

        const rNew_rules = new Map();
        rNew._rules = rNew_rules;

        this.mergedRules._rules.forEach((value, key) => {
          if (value.code) {
            //
            // filter empty rules !!!
            //
            rNew_rules.set(key, value);
          }
        });

        this.config.rules = rNew;
      }

      // console.log("knxProductConfig", this.config);
      // console.log("knxProductConfig to JSON", this.config.toJSON());

      if (!this.config.isEmpty) {
        //
        // save config data to server in a separate blob
        //

        const configSaved = await ProductStorageHandler.uploadProductConfig(
          this.modelDataId,
          JSON.stringify(this.config.toJSON()),
          {},
        );
        if (!configSaved) {
          return false;
        }
        this.productInfo.HasConfig = "true";
      } else {
        this.productInfo.HasConfig = "false";

        // MWE todo 24.7.2024
        // eigentlich müsste man hier vorsorglich noch die Config auf dem Server löschen
        // dazu fehlt aber im Moment nocht der Server Endpoint
        //
      }

      // TODO 12/5/2025
      // Right now set as false by default
      // this.productInfo.NoChannels = "false";
      // this.productInfo.SpecialUsage = "Unset";

      // Adding new entries from ApplicationProgram
      const AP = this.getApplicationProgramIntern(undefined);
      const MV = this.productInfo.MaskVersion;
      if (AP && (MV == "" || MV == undefined)) {
        this.productInfo.MaskVersion = AP.a.MaskVersion?.trim();
        this.productInfo.PeiType = AP.a.PeiType?.trim();
        this.productInfo.LoadProcedureStyle = AP.a.LoadProcedureStyle?.trim();
        this.productInfo.ProgramType = AP.a.ProgramType?.trim();
        this.productInfo.Linkable = AP.a.Linkable?.trim();
        this.productInfo.ApplicationNumber = AP.a.ApplicationNumber?.trim();
        this.productInfo.DynamicTableManagement = AP.a.DynamicTableManagement?.trim();
        this.productInfo.MinEtsVersion = AP.a.MinEtsVersion?.trim();
        this.productInfo.CreatedFromLegacySchemaVersion = AP.a.CreatedFromLegacySchemaVersion?.trim();
        this.productInfo.NonRegRelevantDataVersion = AP.a.NonRegRelevantDataVersion?.trim();
        this.productInfo.ReplacesVersions = AP.a.ReplacesVersions?.trim();
        this.productInfo.Hash = AP.a.Hash?.trim();
      }
      if (AP.a?.OriginalManufacturer) {
        this.productInfo.OriginalManufacturer = AP.a?.OriginalManufacturer;
      }

      //
      // save the metadata to the main blob
      //
      const metadata =
        this.productInfo.ProductNames && !isEncoded(this.productInfo.ProductNames)
          ? getEncodedOriginalMetadata(this.productInfo)
          : this.productInfo;

      return await ProductStorageHandler.updateProductMetaData(this.modelDataId, metadata);
    } else {
      return false;
    }
  }

  consistencyCheck(parent: BaseModel) {
    if (this.modelDataId && this.xmlstring) {
      console.log("DeviceTemplate consistencyCheck: xmlstring found");
      // this.xmlstring = undefined;  // keep it again for ets import. MWE 8.2.2026
    }

    if (this.productInfo.ChannelType) {
      if (
        ![
          ChannelType.Blind,
          ChannelType.Jalousie,
          ChannelType.Switch,
          ChannelType.Dimmer,
          ChannelType.Heating,
          ChannelType.Lock,
          ChannelType.Rgb,
          ChannelType.TuneableWhite,
        ].indexOf(this.productInfo.ChannelType as ChannelType)
      ) {
        (this.parent as BaseModel).logConsistencyError("Invalid DeviceType: " + this.productInfo.ChannelType);
        this.productInfo.ChannelType = undefined;
      }
    }
  }

  getDefaultRule(device: Device, ruleId: RuleIds): KnxRule | undefined {
    if (ruleId == RuleIds.mapSignalToTag) {
      const manufacturerId = this.productInfo.ManufacturerId;
      let typeForRule: "DecentralActor" | "Actor" | "Button" = "Actor";
      if (device.isDecentralActor) {
        typeForRule = "DecentralActor";
      } else if (device.isButton) {
        typeForRule = "Button";
      }
      const sRule = getManufacturRule(typeForRule, manufacturerId || "", ruleId);
      if (sRule) {
        const rule = new KnxRule();
        rule.code = sRule;
        rule.rule_id = ruleId;
        rule.modifiedBy = "system";
        return rule;
      }

      // NoChannels: ignore all signals when no script is present
      if (this.productInfo.NoChannels === "true") {
        const rule = new KnxRule();
        rule.code = `const res = mapSignal({\n  ignore: /.*/,\n})\nreturn res;`;
        rule.rule_id = ruleId;
        rule.modifiedBy = "system";
        return rule;
      }
    }

    return undefined;
  }

  get hasTranslationEN(): boolean {
    if (this.languageMapping.hasTranslationEN) {
      return true;
    }

    return this.productInfo?.DefaultLanguage?.toLowerCase().startsWith("en") || false;
  }
}
